import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import AdminShell from '../components/AdminShell'
import { useAuth } from '../context/AuthContext'
import { authApi, galleryApi, projectsApi } from '../lib/api'
import { galleryCache, projectsCache } from '../lib/cache'
import { filesToDataUrls } from '../utils/image'

const Stack = styled.div`
  display: grid;
  gap: 1rem;
`

const Notice = styled.div`
  border-radius: 10px;
  padding: 0.7rem 0.8rem;
  font-family: 'Lato', sans-serif;
  font-size: 0.92rem;

  ${props =>
    props.$type === 'error'
      ? `
    background: #fee2e2;
    border: 1px solid #fca5a5;
    color: #991b1b;
  `
      : `
    background: #dcfce7;
    border: 1px solid #86efac;
    color: #14532d;
  `}
`

const UploadProgressCard = styled.div`
  border-radius: 10px;
  border: 1px solid #93c5fd;
  background: #eff6ff;
  padding: 0.7rem 0.8rem;
  font-family: 'Lato', sans-serif;
`

const UploadProgressTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.6rem;
  color: #1e3a8a;
  font-size: 0.9rem;

  strong {
    font-weight: 700;
  }
`

const UploadProgressTrack = styled.div`
  margin-top: 0.55rem;
  width: 100%;
  height: 8px;
  background: #dbeafe;
  border-radius: 999px;
  overflow: hidden;
`

const UploadProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #2563eb, #3b82f6);
  transition: width 0.2s ease;
`

const Section = styled.section`
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1rem;

  h2 {
    margin: 0;
    font-family: 'Playfair Display', serif;
    color: #0f172a;
  }

  p {
    color: #475569;
    font-family: 'Lato', sans-serif;
    line-height: 1.6;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0.8rem;
`

const Field = styled.div`
  margin-bottom: 0.8rem;

  label {
    display: block;
    margin-bottom: 0.3rem;
    font-size: 0.86rem;
    color: #334155;
    font-weight: 700;
    font-family: 'Lato', sans-serif;
  }
`

const Input = styled.input`
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 0.7rem 0.75rem;
  font-size: 0.95rem;
`

const Select = styled.select`
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 0.7rem 0.75rem;
  font-size: 0.95rem;
  background: #fff;
`

const TextArea = styled.textarea`
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 0.7rem 0.75rem;
  min-height: 110px;
  resize: vertical;
  font-size: 0.95rem;
`

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`

const Button = styled.button`
  border: none;
  border-radius: 999px;
  padding: 0.55rem 0.85rem;
  font-size: 0.84rem;
  cursor: pointer;
  color: #fff;
  background: ${props => {
    if (props.$tone === 'danger') return '#dc2626'
    if (props.$tone === 'neutral') return '#334155'
    return '#111827'
  }};

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

const SmallText = styled.div`
  margin-top: 0.35rem;
  color: #64748b;
  font-size: 0.8rem;
  font-family: 'Lato', sans-serif;
`

const TableWrap = styled.div`
  overflow: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    text-align: left;
    padding: 0.6rem;
    border-bottom: 1px solid #e2e8f0;
    font-family: 'Lato', sans-serif;
    font-size: 0.88rem;
    vertical-align: top;
  }

  th {
    color: #334155;
  }
`

const ThumbGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(88px, 1fr));
  gap: 0.4rem;
`

const Thumb = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  overflow: hidden;
  background: #f8fafc;

  img {
    width: 100%;
    height: 64px;
    object-fit: cover;
    display: block;
  }
`

const SubsectionTitle = styled.h3`
  margin: 0 0 0.65rem;
  font-family: 'Playfair Display', serif;
  color: #0f172a;
`

function normalizeItems(payload) {
  return Array.isArray(payload?.items) ? payload.items : []
}

function normalizeHeader(payload) {
  return {
    title: payload?.title || 'Projects',
    description: payload?.description || '',
    image: payload?.image || ''
  }
}

function splitLinesToList(value) {
  return value
    .split(/\r?\n/)
    .map(item => item.trim())
    .filter(Boolean)
}

function AdminDashboardPage({ section = 'all' }) {
  const { user } = useAuth()
  const isSuperAdmin = user?.role === 'superAdmin'
  const showGallery = section === 'all' || section === 'gallery'
  const showProjects = section === 'all' || section === 'projects'
  const showUsers = section === 'all' || section === 'users'

  const [isBooting, setIsBooting] = useState(true)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState({ type: '', message: '' })
  const [uploadState, setUploadState] = useState({ isActive: false, label: '', percent: 0 })

  const [galleryItems, setGalleryItems] = useState([])
  const [projectPosts, setProjectPosts] = useState([])
  const [users, setUsers] = useState([])

  const [newGallery, setNewGallery] = useState({ image: '', caption: '', category: '' })
  const [newGalleryFile, setNewGalleryFile] = useState(null)
  const [bulkCategory, setBulkCategory] = useState('')
  const [bulkFiles, setBulkFiles] = useState([])
  const [galleryEditId, setGalleryEditId] = useState('')
  const [galleryEdit, setGalleryEdit] = useState({ image: '', caption: '', category: '' })
  const [galleryEditFile, setGalleryEditFile] = useState(null)

  const [headerForm, setHeaderForm] = useState({ title: 'Projects', description: '', image: '' })
  const [headerImageFile, setHeaderImageFile] = useState(null)

  const [postForm, setPostForm] = useState({ title: '', description: '', link: '', imageCsv: '' })
  const [postFiles, setPostFiles] = useState([])

  const [postEditId, setPostEditId] = useState('')
  const [postEditForm, setPostEditForm] = useState({ title: '', description: '', link: '', imageCsv: '' })
  const [postEditFiles, setPostEditFiles] = useState([])

  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'admin' })

  const galleryById = useMemo(() => {
    const map = new Map()
    galleryItems.forEach(item => {
      if (item?._id) map.set(item._id, item)
    })
    return map
  }, [galleryItems])

  const loadAll = async () => {
    const [galleryPayload, projectsHeaderPayload, projectsPostsPayload, usersPayload] = await Promise.all([
      galleryApi.list({ page: 1, limit: 100 }),
      projectsApi.getHeader(),
      projectsApi.listPosts({ page: 1, limit: 100 }),
      isSuperAdmin ? authApi.listUsers() : Promise.resolve([])
    ])

    setGalleryItems(normalizeItems(galleryPayload))
    setHeaderForm(normalizeHeader(projectsHeaderPayload))
    setProjectPosts(normalizeItems(projectsPostsPayload))
    setUsers(Array.isArray(usersPayload) ? usersPayload : [])
  }

  useEffect(() => {
    let mounted = true

    const boot = async () => {
      try {
        await loadAll()
      } catch (error) {
        if (mounted) {
          setNotice({ type: 'error', message: error.message || 'Failed to load admin dashboard data.' })
        }
      } finally {
        if (mounted) setIsBooting(false)
      }
    }

    boot()

    return () => {
      mounted = false
    }
  }, [isSuperAdmin])

  useEffect(() => {
    if (!galleryEditId) return
    const selected = galleryById.get(galleryEditId)
    if (!selected) return

    setGalleryEdit({
      image: selected.image || '',
      caption: selected.caption || '',
      category: selected.category || ''
    })
  }, [galleryEditId, galleryById])

  const runAction = async (action, options = {}) => {
    const uploadLabel = options.uploadLabel || ''

    setBusy(true)
    setNotice({ type: '', message: '' })

    if (uploadLabel) {
      setUploadState({ isActive: true, label: uploadLabel, percent: 0 })
    }

    try {
      await action(percent => {
        if (!uploadLabel) return
        const safePercent = Math.min(100, Math.max(0, Number(percent) || 0))
        setUploadState(prev => ({ ...prev, percent: safePercent }))
      })

      if (uploadLabel) {
        setUploadState(prev => ({ ...prev, percent: 100 }))
      }
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Action failed.' })
    } finally {
      setBusy(false)

      if (uploadLabel) {
        setTimeout(() => {
          setUploadState({ isActive: false, label: '', percent: 0 })
        }, 300)
      }
    }
  }

  const createGalleryItem = event => {
    event.preventDefault()

    runAction(async onUploadProgress => {
      let image = newGallery.image.trim()
      if (newGalleryFile) {
        const [dataUrl] = await filesToDataUrls([newGalleryFile])
        image = dataUrl || image
      }

      await galleryApi.create({
        image,
        caption: newGallery.caption.trim(),
        category: newGallery.category.trim()
      }, { onUploadProgress })

      const listPayload = await galleryApi.list({ page: 1, limit: 100 })
      setGalleryItems(normalizeItems(listPayload))
      await galleryCache.clearList()
      setNewGallery({ image: '', caption: '', category: '' })
      setNewGalleryFile(null)
      setNotice({ type: 'ok', message: 'Gallery item created.' })
    }, { uploadLabel: 'Uploading gallery item...' })
  }

  const bulkCreateGalleryItems = event => {
    event.preventDefault()

    runAction(async onUploadProgress => {
      const dataUrls = await filesToDataUrls(bulkFiles)
      const items = dataUrls.map(image => ({ image, category: bulkCategory.trim(), caption: '' }))

      if (!items.length) {
        throw new Error('Please select at least one image file for bulk upload.')
      }

      await galleryApi.bulkCreate({ items }, { onUploadProgress })
      const listPayload = await galleryApi.list({ page: 1, limit: 100 })
      setGalleryItems(normalizeItems(listPayload))
      await galleryCache.clearList()
      setBulkCategory('')
      setBulkFiles([])
      setNotice({ type: 'ok', message: 'Bulk gallery upload completed.' })
    }, { uploadLabel: 'Uploading gallery batch...' })
  }

  const updateGalleryItem = event => {
    event.preventDefault()

    if (!galleryEditId) return

    runAction(async onUploadProgress => {
      let image = galleryEdit.image.trim()

      if (galleryEditFile) {
        const [dataUrl] = await filesToDataUrls([galleryEditFile])
        image = dataUrl || image
      }

      await galleryApi.update(galleryEditId, {
        image,
        caption: galleryEdit.caption.trim(),
        category: galleryEdit.category.trim()
      }, { onUploadProgress })

      const listPayload = await galleryApi.list({ page: 1, limit: 100 })
      setGalleryItems(normalizeItems(listPayload))
      await galleryCache.clearList()
      setNotice({ type: 'ok', message: 'Gallery item updated.' })
    }, { uploadLabel: 'Uploading gallery update...' })
  }

  const deleteGalleryItem = id => {
    runAction(async () => {
      await galleryApi.remove(id)
      const listPayload = await galleryApi.list({ page: 1, limit: 100 })
      setGalleryItems(normalizeItems(listPayload))
      await galleryCache.clearList()

      if (id === galleryEditId) {
        setGalleryEditId('')
        setGalleryEdit({ image: '', caption: '', category: '' })
      }

      setNotice({ type: 'ok', message: 'Gallery item deleted.' })
    })
  }

  const updateProjectsHeader = event => {
    event.preventDefault()

    runAction(async onUploadProgress => {
      let image = headerForm.image.trim()

      if (headerImageFile) {
        const [dataUrl] = await filesToDataUrls([headerImageFile])
        image = dataUrl || image
      }

      const payload = {
        title: headerForm.title.trim(),
        description: headerForm.description.trim(),
        image
      }

      const doc = await projectsApi.updateHeader(payload, { onUploadProgress })
      setHeaderForm(normalizeHeader(doc))
      setHeaderImageFile(null)
      await projectsCache.clearAll()
      setNotice({ type: 'ok', message: 'Projects header updated.' })
    }, { uploadLabel: 'Uploading projects header...' })
  }

  const createProjectPost = event => {
    event.preventDefault()

    runAction(async onUploadProgress => {
      const uploadImages = await filesToDataUrls(postFiles)
      const csvImages = splitLinesToList(postForm.imageCsv)

      const payload = {
        title: postForm.title.trim(),
        description: postForm.description.trim(),
        link: postForm.link.trim(),
        images: [...csvImages, ...uploadImages]
      }

      await projectsApi.createPost(payload, { onUploadProgress })
      const listPayload = await projectsApi.listPosts({ page: 1, limit: 100 })
      setProjectPosts(normalizeItems(listPayload))
      await projectsCache.clearAll()
      setPostForm({ title: '', description: '', link: '', imageCsv: '' })
      setPostFiles([])
      setNotice({ type: 'ok', message: 'Project post created.' })
    }, { uploadLabel: 'Uploading project post...' })
  }

  const startEditPost = post => {
    setPostEditId(post._id)
    setPostEditForm({
      title: post.title || '',
      description: post.description || '',
      link: post.link || '',
      imageCsv: Array.isArray(post.images) ? post.images.join('\n') : ''
    })
    setPostEditFiles([])
  }

  const updateProjectPost = event => {
    event.preventDefault()

    if (!postEditId) return

    runAction(async onUploadProgress => {
      const uploadImages = await filesToDataUrls(postEditFiles)
      const csvImages = splitLinesToList(postEditForm.imageCsv)

      const payload = {
        title: postEditForm.title.trim(),
        description: postEditForm.description.trim(),
        link: postEditForm.link.trim(),
        images: [...csvImages, ...uploadImages]
      }

      await projectsApi.updatePost(postEditId, payload, { onUploadProgress })
      const listPayload = await projectsApi.listPosts({ page: 1, limit: 100 })
      setProjectPosts(normalizeItems(listPayload))
      await projectsCache.clearAll()
      setPostEditId('')
      setPostEditForm({ title: '', description: '', link: '', imageCsv: '' })
      setPostEditFiles([])
      setNotice({ type: 'ok', message: 'Project post updated.' })
    }, { uploadLabel: 'Uploading project changes...' })
  }

  const deleteProjectPost = id => {
    runAction(async () => {
      await projectsApi.deletePost(id)
      const listPayload = await projectsApi.listPosts({ page: 1, limit: 100 })
      setProjectPosts(normalizeItems(listPayload))
      await projectsCache.clearAll()

      if (id === postEditId) {
        setPostEditId('')
        setPostEditForm({ title: '', description: '', link: '', imageCsv: '' })
      }

      setNotice({ type: 'ok', message: 'Project post deleted.' })
    })
  }

  const deleteProjectImage = (postId, index) => {
    runAction(async () => {
      await projectsApi.deleteImage(postId, index)
      const listPayload = await projectsApi.listPosts({ page: 1, limit: 100 })
      setProjectPosts(normalizeItems(listPayload))
      await projectsCache.clearAll()
      setNotice({ type: 'ok', message: 'Project image removed.' })
    })
  }

  const createUser = event => {
    event.preventDefault()

    runAction(async () => {
      await authApi.createUser({
        username: newUser.username.trim(),
        password: newUser.password,
        role: newUser.role
      })

      const data = await authApi.listUsers()
      setUsers(Array.isArray(data) ? data : [])
      setNewUser({ username: '', password: '', role: 'admin' })
      setNotice({ type: 'ok', message: 'User created.' })
    })
  }

  const toggleUserActive = (userId, currentState) => {
    runAction(async () => {
      await authApi.setUserActive({ userId, isActive: !currentState })
      const data = await authApi.listUsers()
      setUsers(Array.isArray(data) ? data : [])
      setNotice({ type: 'ok', message: 'User status updated.' })
    })
  }

  if (isBooting) {
    return (
      <AdminShell>
        <Notice>Loading admin dashboard data...</Notice>
      </AdminShell>
    )
  }

  return (
    <AdminShell>
      <Stack>
        {notice.message ? <Notice $type={notice.type}>{notice.message}</Notice> : null}
        {uploadState.isActive ? (
          <UploadProgressCard>
            <UploadProgressTop>
              <strong>{uploadState.label}</strong>
              <span>{uploadState.percent}%</span>
            </UploadProgressTop>
            <UploadProgressTrack>
              <UploadProgressFill style={{ width: `${uploadState.percent}%` }} />
            </UploadProgressTrack>
          </UploadProgressCard>
        ) : null}

        {showGallery ? (
          <Section id="gallery">
          <h2>Gallery Manager</h2>
          <p>Create, bulk upload, update, and delete gallery images.</p>

          <Grid>
            <div>
              <SubsectionTitle>Create Single Item</SubsectionTitle>
              <form onSubmit={createGalleryItem}>
                <Field>
                  <label htmlFor="gallery-image-url">Image URL / Data URL</label>
                  <Input
                    id="gallery-image-url"
                    value={newGallery.image}
                    onChange={event => setNewGallery(prev => ({ ...prev, image: event.target.value }))}
                    placeholder="https://... or data:image/..."
                  />
                </Field>

                <Field>
                  <label htmlFor="gallery-image-file">Image File (optional override)</label>
                  <Input
                    id="gallery-image-file"
                    type="file"
                    accept="image/*"
                    onChange={event => setNewGalleryFile(event.target.files?.[0] || null)}
                  />
                </Field>

                <Field>
                  <label htmlFor="gallery-caption">Caption</label>
                  <Input
                    id="gallery-caption"
                    value={newGallery.caption}
                    onChange={event => setNewGallery(prev => ({ ...prev, caption: event.target.value }))}
                  />
                </Field>

                <Field>
                  <label htmlFor="gallery-category">Category</label>
                  <Input
                    id="gallery-category"
                    value={newGallery.category}
                    onChange={event => setNewGallery(prev => ({ ...prev, category: event.target.value }))}
                  />
                </Field>

                <ButtonRow>
                  <Button type="submit" disabled={busy}>
                    Add Gallery Item
                  </Button>
                </ButtonRow>
              </form>
            </div>

            <div>
              <SubsectionTitle>Bulk Upload</SubsectionTitle>
              <form onSubmit={bulkCreateGalleryItems}>
                <Field>
                  <label htmlFor="gallery-bulk-files">Select Image Files</label>
                  <Input
                    id="gallery-bulk-files"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={event => setBulkFiles(Array.from(event.target.files || []))}
                  />
                </Field>

                <Field>
                  <label htmlFor="gallery-bulk-category">Shared Category</label>
                  <Input
                    id="gallery-bulk-category"
                    value={bulkCategory}
                    onChange={event => setBulkCategory(event.target.value)}
                    placeholder="Team, Projects, Installation, ..."
                  />
                </Field>

                <SmallText>{bulkFiles.length} files selected</SmallText>

                <ButtonRow>
                  <Button type="submit" disabled={busy}>
                    Upload Selected Files
                  </Button>
                </ButtonRow>
              </form>
            </div>
          </Grid>

          <Grid>
            <div>
              <SubsectionTitle>Edit Existing Item</SubsectionTitle>
              <form onSubmit={updateGalleryItem}>
                <Field>
                  <label htmlFor="gallery-edit-id">Select Item</label>
                  <Select
                    id="gallery-edit-id"
                    value={galleryEditId}
                    onChange={event => setGalleryEditId(event.target.value)}
                  >
                    <option value="">Choose an item</option>
                    {galleryItems.map(item => (
                      <option key={item._id} value={item._id}>
                        {(item.category || 'General') + (item.caption ? ` - ${item.caption.slice(0, 35)}` : '')}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field>
                  <label htmlFor="gallery-edit-image">Image URL / Data URL</label>
                  <Input
                    id="gallery-edit-image"
                    value={galleryEdit.image}
                    onChange={event => setGalleryEdit(prev => ({ ...prev, image: event.target.value }))}
                    disabled={!galleryEditId}
                  />
                </Field>

                <Field>
                  <label htmlFor="gallery-edit-file">Replace with Image File</label>
                  <Input
                    id="gallery-edit-file"
                    type="file"
                    accept="image/*"
                    onChange={event => setGalleryEditFile(event.target.files?.[0] || null)}
                    disabled={!galleryEditId}
                  />
                </Field>

                <Field>
                  <label htmlFor="gallery-edit-caption">Caption</label>
                  <Input
                    id="gallery-edit-caption"
                    value={galleryEdit.caption}
                    onChange={event => setGalleryEdit(prev => ({ ...prev, caption: event.target.value }))}
                    disabled={!galleryEditId}
                  />
                </Field>

                <Field>
                  <label htmlFor="gallery-edit-category">Category</label>
                  <Input
                    id="gallery-edit-category"
                    value={galleryEdit.category}
                    onChange={event => setGalleryEdit(prev => ({ ...prev, category: event.target.value }))}
                    disabled={!galleryEditId}
                  />
                </Field>

                <ButtonRow>
                  <Button type="submit" disabled={busy || !galleryEditId}>
                    Save Gallery Changes
                  </Button>
                </ButtonRow>
              </form>
            </div>

            <div>
              <SubsectionTitle>Existing Gallery Items</SubsectionTitle>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Preview</th>
                      <th>Category</th>
                      <th>Caption</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {galleryItems.map(item => (
                      <tr key={item._id}>
                        <td>
                          <Thumb>
                            <img src={item.image} alt={item.caption || 'gallery'} />
                          </Thumb>
                        </td>
                        <td>{item.category || 'General'}</td>
                        <td>{item.caption || '-'}</td>
                        <td>
                          <ButtonRow>
                            <Button type="button" $tone="neutral" onClick={() => setGalleryEditId(item._id)} disabled={busy}>
                              Edit
                            </Button>
                            <Button
                              type="button"
                              $tone="danger"
                              onClick={() => deleteGalleryItem(item._id)}
                              disabled={busy}
                            >
                              Delete
                            </Button>
                          </ButtonRow>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </div>
          </Grid>
          </Section>
        ) : null}

        {showProjects ? (
          <Section id="projects">
          <h2>Projects Manager</h2>
          <p>Update page header and maintain project posts and images.</p>

          <Grid>
            <div>
              <SubsectionTitle>Header Content</SubsectionTitle>
              <form onSubmit={updateProjectsHeader}>
                <Field>
                  <label htmlFor="header-title">Title</label>
                  <Input
                    id="header-title"
                    value={headerForm.title}
                    onChange={event => setHeaderForm(prev => ({ ...prev, title: event.target.value }))}
                  />
                </Field>

                <Field>
                  <label htmlFor="header-description">Description</label>
                  <TextArea
                    id="header-description"
                    value={headerForm.description}
                    onChange={event => setHeaderForm(prev => ({ ...prev, description: event.target.value }))}
                  />
                </Field>

                <Field>
                  <label htmlFor="header-image">Header Image URL / Data URL</label>
                  <Input
                    id="header-image"
                    value={headerForm.image}
                    onChange={event => setHeaderForm(prev => ({ ...prev, image: event.target.value }))}
                  />
                </Field>

                <Field>
                  <label htmlFor="header-image-file">Replace with Image File</label>
                  <Input
                    id="header-image-file"
                    type="file"
                    accept="image/*"
                    onChange={event => setHeaderImageFile(event.target.files?.[0] || null)}
                  />
                </Field>

                <Button type="submit" disabled={busy}>
                  Save Header
                </Button>
              </form>
            </div>

            <div>
              <SubsectionTitle>Create Project Post</SubsectionTitle>
              <form onSubmit={createProjectPost}>
                <Field>
                  <label htmlFor="post-title">Title</label>
                  <Input
                    id="post-title"
                    value={postForm.title}
                    onChange={event => setPostForm(prev => ({ ...prev, title: event.target.value }))}
                    required
                  />
                </Field>

                <Field>
                  <label htmlFor="post-description">Description</label>
                  <TextArea
                    id="post-description"
                    value={postForm.description}
                    onChange={event => setPostForm(prev => ({ ...prev, description: event.target.value }))}
                  />
                </Field>

                <Field>
                  <label htmlFor="post-link">Project Link</label>
                  <Input
                    id="post-link"
                    value={postForm.link}
                    onChange={event => setPostForm(prev => ({ ...prev, link: event.target.value }))}
                    placeholder="https://"
                  />
                </Field>

                <Field>
                  <label htmlFor="post-images-csv">Images (one URL/Data URL per line)</label>
                  <TextArea
                    id="post-images-csv"
                    value={postForm.imageCsv}
                    onChange={event => setPostForm(prev => ({ ...prev, imageCsv: event.target.value }))}
                  />
                </Field>

                <Field>
                  <label htmlFor="post-images-file">Upload Images</label>
                  <Input
                    id="post-images-file"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={event => setPostFiles(Array.from(event.target.files || []))}
                  />
                </Field>

                <SmallText>{postFiles.length} files selected</SmallText>

                <Button type="submit" disabled={busy}>
                  Add Project Post
                </Button>
              </form>
            </div>
          </Grid>

          <div>
            <SubsectionTitle>Project Posts</SubsectionTitle>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Description</th>
                    <th>Images</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projectPosts.map(post => (
                    <tr key={post._id}>
                      <td>{post.title}</td>
                      <td>{post.description || '-'}</td>
                      <td>
                        <ThumbGrid>
                          {(post.images || []).map((image, index) => (
                            <Thumb key={`${post._id}-${index}`}>
                              <img src={image} alt={`${post.title} ${index + 1}`} />
                              <Button
                                type="button"
                                $tone="danger"
                                onClick={() => deleteProjectImage(post._id, index)}
                                disabled={busy}
                              >
                                Remove
                              </Button>
                            </Thumb>
                          ))}
                        </ThumbGrid>
                      </td>
                      <td>
                        <ButtonRow>
                          <Button type="button" $tone="neutral" onClick={() => startEditPost(post)} disabled={busy}>
                            Edit
                          </Button>
                          <Button type="button" $tone="danger" onClick={() => deleteProjectPost(post._id)} disabled={busy}>
                            Delete
                          </Button>
                        </ButtonRow>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </div>

          {postEditId ? (
            <div>
              <SubsectionTitle>Edit Project Post</SubsectionTitle>
              <form onSubmit={updateProjectPost}>
                <Grid>
                  <div>
                    <Field>
                      <label htmlFor="post-edit-title">Title</label>
                      <Input
                        id="post-edit-title"
                        value={postEditForm.title}
                        onChange={event => setPostEditForm(prev => ({ ...prev, title: event.target.value }))}
                      />
                    </Field>

                    <Field>
                      <label htmlFor="post-edit-description">Description</label>
                      <TextArea
                        id="post-edit-description"
                        value={postEditForm.description}
                        onChange={event => setPostEditForm(prev => ({ ...prev, description: event.target.value }))}
                      />
                    </Field>
                  </div>

                  <div>
                    <Field>
                      <label htmlFor="post-edit-link">Project Link</label>
                      <Input
                        id="post-edit-link"
                        value={postEditForm.link}
                        onChange={event => setPostEditForm(prev => ({ ...prev, link: event.target.value }))}
                      />
                    </Field>

                    <Field>
                      <label htmlFor="post-edit-images">Images (one URL/Data URL per line)</label>
                      <TextArea
                        id="post-edit-images"
                        value={postEditForm.imageCsv}
                        onChange={event => setPostEditForm(prev => ({ ...prev, imageCsv: event.target.value }))}
                      />
                    </Field>

                    <Field>
                      <label htmlFor="post-edit-files">Append Uploaded Images</label>
                      <Input
                        id="post-edit-files"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={event => setPostEditFiles(Array.from(event.target.files || []))}
                      />
                    </Field>
                  </div>
                </Grid>

                <ButtonRow>
                  <Button type="submit" disabled={busy}>
                    Save Post Changes
                  </Button>
                  <Button
                    type="button"
                    $tone="neutral"
                    onClick={() => {
                      setPostEditId('')
                      setPostEditForm({ title: '', description: '', link: '', imageCsv: '' })
                      setPostEditFiles([])
                    }}
                    disabled={busy}
                  >
                    Cancel Edit
                  </Button>
                </ButtonRow>
              </form>
            </div>
          ) : null}
          </Section>
        ) : null}

        {showUsers ? (
          <Section id="users">
          <h2>User Management</h2>
          {isSuperAdmin ? (
            <>
              <p>Manage admin accounts and active status.</p>

              <Grid>
                <div>
                  <SubsectionTitle>Create User</SubsectionTitle>
                  <form onSubmit={createUser}>
                    <Field>
                      <label htmlFor="new-username">Username</label>
                      <Input
                        id="new-username"
                        value={newUser.username}
                        onChange={event => setNewUser(prev => ({ ...prev, username: event.target.value }))}
                        required
                      />
                    </Field>

                    <Field>
                      <label htmlFor="new-password">Password</label>
                      <Input
                        id="new-password"
                        type="password"
                        value={newUser.password}
                        onChange={event => setNewUser(prev => ({ ...prev, password: event.target.value }))}
                        required
                      />
                    </Field>

                    <Field>
                      <label htmlFor="new-role">Role</label>
                      <Select
                        id="new-role"
                        value={newUser.role}
                        onChange={event => setNewUser(prev => ({ ...prev, role: event.target.value }))}
                      >
                        <option value="admin">admin</option>
                        <option value="superAdmin">superAdmin</option>
                      </Select>
                    </Field>

                    <Button type="submit" disabled={busy}>
                      Create User
                    </Button>
                  </form>
                </div>

                <div>
                  <SubsectionTitle>Existing Users</SubsectionTitle>
                  <TableWrap>
                    <Table>
                      <thead>
                        <tr>
                          <th>Username</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map(item => (
                          <tr key={item._id}>
                            <td>{item.username}</td>
                            <td>{item.role}</td>
                            <td>{item.isActive ? 'Active' : 'Disabled'}</td>
                            <td>
                              <Button
                                type="button"
                                $tone={item.isActive ? 'danger' : 'neutral'}
                                onClick={() => toggleUserActive(item._id, item.isActive)}
                                disabled={busy}
                              >
                                {item.isActive ? 'Disable' : 'Enable'}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                </div>
              </Grid>
            </>
          ) : (
            <p>Your account role is <strong>admin</strong>. User management is only available to <strong>superAdmin</strong>.</p>
          )}
          </Section>
        ) : null}
      </Stack>
    </AdminShell>
  )
}

export default AdminDashboardPage
